/* SFD — put the page's own trust strap into the sticky bar.
 *
 * THE PROBLEM. There were two different trust straps on screen at once. The
 * page has the real one: a stock `multicolumn-icon` section, five items with
 * icons, whose copy lives in each template's JSON and is editable in the
 * theme editor. The sticky bar had its own: two lines of white text on a blue
 * band, hard-coded in Liquid, saying something different, editable only in
 * the code.
 *
 * Lee, 1 Nov: "It needs ideally the same trust strap as on the pages, take it
 * from the home page if you can."
 *
 * THE APPROACH. Clone what the page already rendered rather than write the
 * copy out again. The text is already duplicated across eight template JSONs;
 * a ninth copy buried in a snippet is the last thing this theme needs. Clone
 * it and the bar follows whatever Lee edits, on every template, for free.
 *
 * It runs for every sticky bar on the page, because there are four separate
 * implementations of that bar (the stock snippet plus one in each of the
 * three configurator sections) and they all emit the same class names.
 *
 * If the page has no strap, nothing happens and the bar keeps the wording it
 * already had. That is the intended fallback, not a failure.
 *
 * Loaded from snippets/meta-tags.liquid, per this theme's convention:
 * stylesheets in layout/theme.liquid, scripts here.
 */
(function () {
  'use strict';

  var SRC = '.multicolumn-icon .multicolumn__items';
  var STRIP = '.sticky-atc-bar__trust-strip';
  var BAR = '.sticky-atc-bar-top-wrapper';
  var DONE = 'sticky-atc-bar__trust-strip--icons';

  /* Every sticky bar that should have a strap, whether its own section
     bothered to render one or not.

     The shelving configurator does not. Its section wraps the strip in
     `{% if s.sticky_trust_left != blank or s.sticky_trust_mid != blank %}`
     and those two settings are empty on that template, so the element never
     exists: the shelving bar came out 146px with no strap while every other
     page was 196px with one. Lee, 2 Nov: "the shelving pages are not picking
     up the same sticky cart... the sticky cart should be the same one
     throughout the site."

     Making the strip here rather than fixing one template's settings is the
     point. The bar is one component with four implementations; leaving the
     strap at the mercy of per section settings is what let them drift apart
     in the first place. */
  function targets() {
    var found = [].slice.call(document.querySelectorAll(STRIP));

    [].forEach.call(document.querySelectorAll(BAR), function (bar) {
      if (bar.querySelector(STRIP)) return;
      var made = document.createElement('div');
      made.className = 'sticky-atc-bar__trust-strip';
      bar.appendChild(made);
      found.push(made);
    });

    return found;
  }

  function fill() {
    /* Checked before anything is created, so a page with no strap of its own
       never gets an empty grey band bolted to the bottom of its bar. */
    var src = document.querySelector(SRC);
    if (!src || !src.children.length) return false;

    var strips = targets();
    if (!strips.length) return false;

    /* The live images have already resolved a src from their srcset. Read
       them once, here, rather than per clone. */
    var live = src.querySelectorAll('img');
    var filled = 0;

    [].forEach.call(strips, function (strip) {
      if (strip.classList.contains(DONE)) return;

      var clone = src.cloneNode(true);

      /* The originals fade and zoom in when scrolled to. A clone is a fresh,
         un-upgraded custom element, so it would start hidden and never be
         told to appear: the strap would be there in the DOM and invisible on
         screen. Strip the motion and force it visible. */
      [].forEach.call(clone.querySelectorAll('[data-motion]'), function (m) {
        m.removeAttribute('data-motion');
        m.style.opacity = '1';
        m.style.transform = 'none';
      });

      /* Same reasoning for the lazy loader: pin each image to the src the
         live one already settled on, so nothing has to load again. */
      [].forEach.call(clone.querySelectorAll('img'), function (img, i) {
        var from = live[i];
        if (from && (from.currentSrc || from.src)) {
          img.setAttribute('src', from.currentSrc || from.src);
          img.removeAttribute('srcset');
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
        }
        img.setAttribute('loading', 'eager');
        img.classList.remove('motion-reduce');
      });

      strip.classList.add(DONE);
      strip.textContent = '';
      strip.appendChild(clone);
      filled += 1;
    });

    return filled > 0;
  }

  function start() {
    if (fill()) return;

    /* Three of the four bars are built by their section's own JavaScript, so
       the strip can arrive after this script has run. Watch for it, and stop
       watching once it is done or after ten seconds, whichever comes first,
       so a page with no sticky bar is not left with an observer running. */
    var obs = new MutationObserver(function () {
      if (fill()) obs.disconnect();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
